"use client";

import { useEffect } from "react";
import type { FieldErrors, UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { getProviderConfig } from "@/lib/utils/provider-config";
import { COMPOSE_LAST_FROM_ACCOUNT_KEY } from "@/lib/hooks/compose/useCompose";
import type {
  ComposeClientContextValue,
  ComposeRecipientsContextValue,
} from "@/lib/core/crm/types";
import type { ComposeFormValues, EmailConnection } from "@/lib/types/compose-modal-types";
import { ClientEmailSelector } from "../ClientEmailSelector";
import { UnifiedSubjectField } from "./UnifiedSubjectField";

interface ComposeMainFieldsProps {
  clientState: ComposeClientContextValue;
  emailConnections?: EmailConnection[];
  form: UseFormReturn<ComposeFormValues>;
  formErrors: FieldErrors<ComposeFormValues>;
  /**
   * True for reply/forward (an `originalMessage` is present) — see
   * useComposeFormFieldsState. A reply/forward already targets an existing
   * conversation, so the operator shouldn't have to actively pick a "From"
   * account each time; the field is auto-filled and locked instead of left as
   * an active, required picker with no correct answer (see the support-inbox
   * "From account" bug — none of the connected personal mailboxes IS the
   * shared group inbox the thread actually lives in).
   */
  hasReplyRestrictions?: boolean;
  recipientsState: ComposeRecipientsContextValue;
}

export function ComposeMainFields({
  clientState,
  emailConnections,
  form,
  formErrors,
  hasReplyRestrictions = false,
  recipientsState,
}: ComposeMainFieldsProps) {
  const {
    selectedClient,
    clientOptions,
    filteredHubUsers,
    hubUsersLoading,
    clientsLoading,
    isSearching,
    clientSearchTerm,
    onClientSearchChange,
  } = clientState;
  const { recipients, onRecipientsChange } = recipientsState;

  // Locked on reply/forward → make sure some connected account is actually
  // selected (the field can no longer be fixed by hand), falling back to the
  // first connection if the current value isn't one of them.
  useEffect(() => {
    if (!hasReplyRestrictions) return;
    const current = form.getValues("fromAccount");
    const stillConnected = (emailConnections || []).some(
      (connection) => connection.connectionId === current
    );
    if (stillConnected) return;
    const fallback = emailConnections?.[0]?.connectionId;
    if (fallback) form.setValue("fromAccount", fallback);
  }, [hasReplyRestrictions, emailConnections, form]);

  return (
    <>
      <ClientEmailSelector
        form={form}
        fieldName="to"
        label="To"
        placeholder="Search clients or enter email addresses..."
        helperText="Press Enter to add email, or pick from results."
        clientOptions={clientOptions}
        filteredHubUsers={filteredHubUsers}
        hubUsersLoading={hubUsersLoading}
        clientsLoading={clientsLoading}
        isSearching={isSearching}
        clientSearchTerm={clientSearchTerm}
        onClientSearchChange={onClientSearchChange}
        selectedRecipients={recipients}
        onRecipientsChange={onRecipientsChange}
      />

      <div className="grid gap-4 xl:grid-cols-12">
        {/* min-w-0: grid items default to min-width:auto, so the long "From
            Account" select label would force this column wider than its track
            and overlap the Subject field (clipping it to "…ubject"). */}
        <div className="min-w-0 xl:col-span-4">
          <FormField
            control={form.control}
            name="fromAccount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>From Account</FormLabel>
                <FormControl>
                  <ResponsiveCommandSelect
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      try {
                        if (typeof window !== "undefined" && value) {
                          window.localStorage.setItem(COMPOSE_LAST_FROM_ACCOUNT_KEY, value);
                        }
                      } catch {
                        // Ignore localStorage errors (private browsing, etc.)
                      }
                    }}
                    options={(emailConnections || []).map((connection) => {
                      const providerConfig = getProviderConfig(connection.provider);
                      return {
                        value: connection.connectionId,
                        label: `${providerConfig.name} - ${connection.email || "No email"}`,
                      };
                    })}
                    placeholder="Select email account"
                    title="From Account"
                    description="Choose which email account to send from"
                    expandInline={true}
                    disabled={hasReplyRestrictions}
                  />
                </FormControl>
                {hasReplyRestrictions && (
                  <p className="text-xs text-muted-foreground">
                    Sending account is locked for replies and forwards in this modal.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="min-w-0 xl:col-span-8">
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Subject</FormLabel>
                <FormControl>
                  <UnifiedSubjectField
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Email Subject"
                    selectedClient={selectedClient}
                    hasError={!!formErrors.subject}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </>
  );
}
